import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useHistory, useParams, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Container, FormGroup } from 'react-bootstrap';
import { MultiSelect } from 'react-multi-select-component';
import { useTranslation } from 'react-i18next';
import { Typeahead } from 'react-bootstrap-typeahead';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { cloneDeep, isEmpty } from 'lodash';
import * as systemMasterAction from '../../../SystemMasters/Store/SystemMasters.action';
import * as customModuleAction from '../Store/CustomModule.action';
import '../CustomModule.css';
import Loader from '../../../../Components/Utility/Loader/Loader';
import LibraryCard from '../Library/LibraryCard';
import { checkArray } from '../../../../Components/helper';
import ConfirmationPopup from '../../../../Components/ModalPopup/ConfirmationPopup';
import { toasterMsg } from '../../../../Components/Toaster/Toaster';
import DragDropSort from '../../../../Components/DragDropSort/DragDropSort';
import { DeleteIcon, ExpandIcon } from '../../../../bombiix-icon';
import { NewInput } from '../../../../Components/PIM/common/NewInput';

function AddEdit() {
  const reducer = useSelector((reducerstate) => reducerstate);
  const { UserDetails, SystemMastersDetails, CustomModuleReducer } = reducer;
  const { brandList, pcTypeList, multiSubBrandList } = SystemMastersDetails;
  const { userInfo } = UserDetails;
  const { customLibraryList } = CustomModuleReducer;
  const typeaheadRef = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { moduleGrpId } = useParams();
  const location = useLocation();
  const stateData = location.state;

  const [state, setState] = useState({
    isEditModeOn: false,
    pageLoading: false,
    groupDetails: {},
    libraryData: [],
    selectedLibrary: [],
    deleteModal: false,
    reArrange: false,
    subBrandAccess: false,
  });
  const { isEditModeOn, pageLoading, groupDetails, libraryData, selectedLibrary, deleteModal, reArrange } = state;
  const history = useHistory();
  const brandsOptions = brandList?.map((ele) => {
    const obj = {};
    obj.label = ele.name;
    obj.value = ele._id;
    return obj;
  });
  const subBrandOptions = multiSubBrandList?.map((ele) => {
    const obj = {};
    obj.label = ele.name;
    obj.value = ele._id;
    obj.isActive = ele.isActive;
    return obj;
  });

  const productTypeOptions = pcTypeList?.map((ele) => {
    const obj = {};
    obj.label = ele.name;
    obj.value = ele._id;
    return obj;
  });

  // Memoize brandIds to prevent unnecessary re-renders and API calls
  const memoizedBrandIds = useMemo(() => {
    if (groupDetails?.brandId && groupDetails.brandId.length > 0) {
      return groupDetails.brandId.map((brand) => brand.value || brand);
    }
    return [];
  }, [groupDetails?.brandId]);

  useEffect(() => {
    const fetchSubBrandAccess = async () => {
      try {
        const subBrandAccessRes = await dispatch(systemMasterAction.getSubBrandAccess());
        setState((prevState) => ({
          ...prevState,
          subBrandAccess: subBrandAccessRes?.data || false,
        }));
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          subBrandAccess: false,
        }));
      }
    };
    fetchSubBrandAccess();
  }, [dispatch]);
  // Fetch sub-brands when selected brands change
  useEffect(() => {
    const fetchSubBrands = async () => {
      if (memoizedBrandIds.length > 0) {
        const queryParams = {
          brandId: memoizedBrandIds,
        };
        await dispatch(systemMasterAction.getMultiSubBrandDetails(queryParams));
      } else {
        await dispatch(systemMasterAction.getMultiSubBrandDetails({}));
      }
    };

    fetchSubBrands();
  }, [dispatch, memoizedBrandIds]);
  useEffect(() => {
    const getGroupData = async () => {
      const res = await dispatch(customModuleAction.getCustomModuleGroupById(moduleGrpId));
      if (res.code === 200) {
        setState((prevState) => ({
          ...prevState,
          groupDetails: res?.data,
        }));
        setState((prevState) => ({
          ...prevState,
          pageLoading: false,
        }));
      }
    };
    if (moduleGrpId) {
      setState((prevState) => ({
        ...prevState,
        isEditModeOn: true,
        pageLoading: true,
      }));
      getGroupData();
    }
  }, [moduleGrpId]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        dispatch(
          systemMasterAction.getPCTypesDetails({
            loggedInUserId: userInfo?.userId,
            isActive: true,
            tier: 'Tier1',
          }),
        ),

        dispatch(
          systemMasterAction.getBrandDetails({
            loggedInUserId: userInfo?.userId,
            isActive: 1,
          }),
        ),
        dispatch(customModuleAction.getAllCustomModule({ isAllRecord: true })),
      ]);
    };

    init();
  }, []);

  useEffect(() => {
    if (stateData?.selectedLibrary?.length > 0) {
      const moduleData = cloneDeep(groupDetails);
      moduleData.libIds = stateData?.selectedLibrary.map((libId) => ({ libId }));
      setState((prevState) => ({
        ...prevState,
        groupDetails: moduleData,
      }));
    }
  }, [stateData?.selectedLibrary]);

  useEffect(() => {
    if (groupDetails?.libIds?.length > 0 && customLibraryList?.length > 0) {
      const libIdsSequence = groupDetails.libIds.map((item) => item.libId);
      const filteredLibs = customLibraryList.filter((item) => groupDetails?.libIds?.some((libIdItem) => item._id === libIdItem.libId));

      const sortedData = filteredLibs.sort((a, b) => {
        const indexA = libIdsSequence.indexOf(a._id);
        const indexB = libIdsSequence.indexOf(b._id);
        return indexA - indexB;
      });

      setState((prevState) => ({
        ...prevState,
        libraryData: sortedData,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        libraryData: [],
      }));
    }
  }, [groupDetails?.libIds, customLibraryList]);

  const isValidForm = (formData) => {
    const errorFields = [];
    if (formData?.groupName?.trim() === '' || !formData.hasOwnProperty('groupName')) {
      errorFields.push('groupName');
    }
    if (formData?.productTypeId?.length === 0 || formData?.productTypeId === null || !formData.hasOwnProperty('productTypeId')) {
      errorFields.push('productTypeId');
    }
    const moduleData = cloneDeep(groupDetails);
    moduleData.errorFields = errorFields;

    setState((prevState) => ({
      ...prevState,
      groupDetails: moduleData,
    }));
    return errorFields?.length <= 0;
  };

  const addEditModule = async () => {
    if (isValidForm(groupDetails)) {
      const groupData = cloneDeep(groupDetails);
      if (groupData?.subBrandId) {
        groupData.subBrandId = groupData?.subBrandId.map((item) => item.value);
      }
      groupData.productTypeId = groupData?.productTypeId.map((item) => item.value);
      if (groupData?.brandId) {
        groupData.brandId = groupData?.brandId.map((item) => item.value);
      }
      if (isEditModeOn) {
        const resUpdate = await dispatch(customModuleAction.updateCustomModuleGroup(groupData, moduleGrpId, t));
        if (resUpdate?.code === 200) {
          history.push('/libraries/custom-module/group');
        }
      } else {
        const res = await dispatch(customModuleAction.addCustomModuleGroup(groupData, t));
        if (res?.code === 200) {
          history.push(`/libraries/custom-module/group/edit/${res?.data?.insertedId}`);
        }
      }
    }
  };

  const handleBasicDetail = (key, value) => {
    const newObject = cloneDeep(groupDetails);
    newObject[key] = value?._id ? value._id : value;

    // If brandId is being updated, filter out sub-brands that don't belong to selected brands
    if (key === 'brandId') {
      const selectedBrandIds = (value || []).map((brand) => brand.value || brand);

      // Filter existing sub-brand selections to only keep those that belong to selected brands
      if (newObject.subBrandId && newObject.subBrandId.length > 0) {
        const validSubBrands = newObject.subBrandId.filter((subBrand) => {
          const subBrandValue = subBrand.value || subBrand;
          // Find the sub-brand in multiSubBrandList to check its parent brand
          const subBrandData = multiSubBrandList?.find((sb) => sb._id === subBrandValue);
          return subBrandData && selectedBrandIds.includes(subBrandData.brandId);
        });
        newObject.subBrandId = validSubBrands;
      }
    }

    setState((oldState) => ({
      ...oldState,
      groupDetails: newObject,
    }));
  };

  const onAutoSuggestion = (suggestion) => {
    if (suggestion?.length > 0) {
      typeaheadRef.current.clear();
      const data = cloneDeep(groupDetails);
      if (data?.libIds?.includes(suggestion[0]?._id)) {
        toasterMsg(t('CUSTOM_MODULES.GROUP.ADD_EDIT.SELECTED_LIBRARY'), 'danger');
      } else {
        if (!isEmpty(data.libIds)) {
          data.libIds = [...data.libIds, { libId: suggestion[0]?._id }];
        } else {
          data.libIds = [{ libId: suggestion[0]?._id }];
        }
        setState((prevState) => ({
          ...prevState,
          groupDetails: data,
        }));
      }
    }
  };

  const handleCheckLibrary = async (isLibChecked, libId) => {
    const existingIds = selectedLibrary;
    if (isLibChecked && !existingIds.includes(libId)) {
      setState((prevState) => ({
        ...prevState,
        selectedLibrary: [...existingIds, libId],
      }));
    } else {
      const removedArray = existingIds?.filter((eleId) => eleId !== libId);

      if (checkArray(removedArray)) {
        setState((prevState) => ({
          ...prevState,
          selectedLibrary: removedArray,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          selectedLibrary: [],
        }));
      }
    }
  };
  const handleDeleteById = (libId) => {
    setState((prevState) => ({
      ...prevState,
      selectedLibrary: [libId],
      deleteModal: true,
    }));
  };

  const handleBulkDelete = () => {
    setState((prevState) => ({
      ...prevState,
      deleteModal: true,
    }));
  };

  const handleRearrange = () => {
    setState((prevState) => ({
      ...prevState,
      reArrange: true,
    }));
  };

  const handleCloseDeleteModal = () => {
    setState((prevState) => ({
      ...prevState,
      selectedLibrary: [],
      deleteModal: false,
    }));
  };

  const confirmDelete = async () => {
    const data = cloneDeep(groupDetails);
    const filteredData = data?.libIds.filter((item) => !selectedLibrary.includes(item?.libId));
    data.libIds = filteredData;
    setState((prevState) => ({
      ...prevState,
      groupDetails: data,
      deleteModal: false,
      selectedLibrary: [],
    }));
  };

  const handleShowRearrangeModal = () => {
    setState((prevState) => ({
      ...prevState,
      reArrange: !reArrange,
    }));
  };

  const handleSaveSequence = async (_e, sequenceList) => {
    const data = cloneDeep(groupDetails);

    const newSeqList = sequenceList?.sequence.map((ele) => {
      return {
        sequence: ele.sequence,
        libId: ele._id,
      };
    });
    data.libIds = newSeqList;

    setState((prevState) => ({
      ...prevState,
      libraryData: sequenceList?.sequence,
      groupDetails: data,
    }));
    handleShowRearrangeModal();
  };

  return (
    <>
      <div className="page-title-filter sticky">
        <div className="title-container d-flex align-items-center">
          <h4 className="title">{t('CUSTOM_MODULES.GROUP.ADD_EDIT.PAGE_TITLE')}</h4>
        </div>
        <div className="search-filter care-library-filter">
          <button type="button" onClick={() => addEditModule()} className="btn btn-white btn-back-lib mb-0">
            {isEditModeOn ? t('CUSTOM_MODULES.GROUP.ADD_EDIT.UPDATE') : t('CUSTOM_MODULES.GROUP.ADD_EDIT.SAVE')}
          </button>
          <Link className="btn btn-white btn-back-lib mb-0 text-decoration-none" to="/libraries/custom-module/group">
            {t('CUSTOM_MODULES.GROUP.ADD_EDIT.CANCEL')}
          </Link>
        </div>
      </div>

      <Container className="position-relative pt-3" fluid>
        {pageLoading ? (
          <Loader />
        ) : (
          <>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <div className="input-group form-floating-label fixed-label">
                    <NewInput
                      type="text"
                      name="groupName"
                      value={groupDetails?.groupName}
                      onChange={(e) => handleBasicDetail('groupName', e.target.value)}
                      placeholder={t('CUSTOM_MODULES.GROUP.ADD_EDIT.NAME')}
                    />
                    <label className="asterisk-req">
                      {t('CUSTOM_MODULES.GROUP.ADD_EDIT.GROUP_NAME')}
                      <span>*</span>
                    </label>
                  </div>
                  {groupDetails?.errorFields?.includes('groupName') && (
                    <p className="mb-0 error-message">{t('CUSTOM_MODULES.GROUP.ADD_EDIT.NAME_IS_REQUIRED')}</p>
                  )}
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <div className="custom-react-select select-min-height">
                    <label className="f-13 m-0 asterisk-req">{t('CUSTOM_MODULES.GROUP.ADD_EDIT.BRANDS')}</label>
                    <MultiSelect
                      options={brandsOptions}
                      className="custom-multiselect w-100"
                      value={groupDetails?.brandId || []}
                      labelledBy={t('CUSTOM_MODULES.GROUP.ADD_EDIT.SELECT_BRAND')}
                      overrideStrings={{
                        selectSomeItems: t('CUSTOM_MODULES.GROUP.ADD_EDIT.SELECT_BRAND'),
                        search: t('CUSTOM_MODULES.GROUP.ADD_EDIT.SEARCH_BRAND'),
                      }}
                      ClearSelectedIcon={null}
                      onChange={(selected) => {
                        handleBasicDetail('brandId', selected);
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
              {state?.subBrandAccess && (
                <Col md={4}>
                  <FormGroup>
                    <div className="custom-react-select select-min-height">
                      <label className="f-13 m-0 asterisk-req">{t('CUSTOM_MODULES.GROUP.ADD_EDIT.SUB_BRAND')}</label>
                      <MultiSelect
                        options={subBrandOptions?.filter((ele) => ele.isActive)}
                        className="custom-multiselect w-100"
                        value={groupDetails?.subBrandId || []}
                        labelledBy={t('CUSTOM_MODULES.GROUP.ADD_EDIT.SELECT_BRAND')}
                        overrideStrings={{
                          selectSomeItems: t('CUSTOM_MODULES.GROUP.ADD_EDIT.SELECT_BRAND'),
                          search: t('CUSTOM_MODULES.GROUP.ADD_EDIT.SEARCH_BRAND'),
                        }}
                        ClearSelectedIcon={null}
                        onChange={(selected) => {
                          handleBasicDetail('subBrandId', selected);
                        }}
                      />
                    </div>
                  </FormGroup>
                </Col>
              )}
              <Col md={4}>
                <FormGroup>
                  <div className="custom-react-select select-min-height">
                    <label className="f-13 m-0 asterisk-req">
                      {t('CUSTOM_MODULES.GROUP.ADD_EDIT.PRODUCT_TYPES')}
                      <span>*</span>
                    </label>
                    <MultiSelect
                      options={productTypeOptions}
                      className="custom-multiselect w-100"
                      value={groupDetails?.productTypeId || []}
                      labelledBy={t('CUSTOM_MODULES.GROUP.ADD_EDIT.SELECT_PRODUCT_TYPES')}
                      overrideStrings={{
                        selectSomeItems: t('CUSTOM_MODULES.GROUP.ADD_EDIT.SELECT_PRODUCT_TYPES'),
                        search: t('CUSTOM_MODULES.GROUP.ADD_EDIT.SEARCH_PRODUCT_TYPE'),
                      }}
                      ClearSelectedIcon={null}
                      onChange={(selected) => {
                        handleBasicDetail('productTypeId', selected);
                      }}
                    />
                  </div>
                  {groupDetails?.errorFields?.includes('productTypeId') && (
                    <p className="mb-0 error-message">{t('CUSTOM_MODULES.GROUP.ADD_EDIT.PRODUCT_TYPE_REQUIRED')}</p>
                  )}
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup className="mb-0">
                  <div className="input-group form-floating-label fixed-label">
                    <NewInput
                      type="text"
                      name="additionalInfo"
                      value={groupDetails?.additionalInfo}
                      onChange={(e) => handleBasicDetail('additionalInfo', e.target.value)}
                      placeholder={t('CUSTOM_MODULES.GROUP.ADD_EDIT.ADDITIONAL_INFO')}
                    />
                    <label className="asterisk-req">{t('CUSTOM_MODULES.GROUP.ADD_EDIT.ADDITIONAL_INFO')}</label>
                  </div>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="group-library-filter my-4 w-300px">
                  <Typeahead
                    ref={typeaheadRef}
                    id="typeheadCustomLibrary"
                    className="custom-AsyncTypeHead"
                    labelKey="moduleName"
                    minLength={1}
                    options={customLibraryList}
                    promptText={t('CUSTOM_MODULES.GROUP.ADD_EDIT.NO_CUSTOM_MODULE_FOUND')}
                    placeholder={t('CUSTOM_MODULES.GROUP.ADD_EDIT.SEARCH_CUSTOM_MODULE')}
                    useCache={false}
                    onChange={(selected) => onAutoSuggestion(selected)}
                    renderMenuItemChildren={(option) => (
                      <div className="d-flex" key={option._id}>
                        <div className="list-text">{option?.moduleName}</div>
                      </div>
                    )}
                  />
                  <button type="button" disabled={selectedLibrary.length === 0} onClick={handleBulkDelete} className="btn btn-outline">
                    <DeleteIcon size={24} />
                  </button>
                  {/* )} */}
                  <button type="button" disabled={libraryData.length < 2} onClick={handleRearrange} className="btn btn-outline">
                    <ExpandIcon size={24} className="m-1" />
                  </button>
                </div>
              </Col>
            </Row>
            <Row>
              {checkArray(libraryData) &&
                libraryData.map((ele, i) => {
                  return (
                    <Col key={ele} md={3}>
                      <LibraryCard
                        handleDeleteById={handleDeleteById}
                        selectedLibrary={selectedLibrary}
                        handleCheckLibrary={handleCheckLibrary}
                        hideEditDuplicate
                        ele={ele}
                        i={i}
                        subBrandAccess={state?.subBrandAccess}
                      />
                    </Col>
                  );
                })}
            </Row>
          </>
        )}
      </Container>
      {deleteModal && (
        <ConfirmationPopup
          onOpen={deleteModal}
          onConfirm={() => confirmDelete()}
          title={t('CUSTOM_MODULES.GROUP.ADD_EDIT.DELETE_MODULE_LIBRARY')}
          message={t('CUSTOM_MODULES.GROUP.ADD_EDIT.DELETE_MODULE_LIBRARY_CONFIRM')}
          toggle={() => handleCloseDeleteModal()}
        />
      )}
      {reArrange && (
        <DragDropSort
          isShow={reArrange}
          isToggle={handleShowRearrangeModal}
          fromComp="customModuleGroup"
          itemList={libraryData}
          saveSequence={handleSaveSequence}
        />
      )}
    </>
  );
}

export default AddEdit;
